import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";

import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { IProduct } from "redux/reducers/models/product";
import { IOrder } from "redux/reducers/models/order";
import useLocalStorage from "hooks/useLocalStorage";

import { ButtonNew as Button } from "components/buttonNew";
import Input from "components/form/input/Input";
import Icon from "components/icon/Icon";
import {
  faArrowLeft,
  faArrowRight,
  faBarcodeRead,
  faRefresh,
  faShoppingCart,
  faUser,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { FolderMenu } from "components/folderMenu/FolderMenu";
import { PRODUCT_FOLDERS_KEY } from "../../settings/folders/Folders";
import { IRegisterGroup } from "../../Admin";

import "elements/register/register.scss";
import { GetVariant } from "api/rpc/2024-04/facilityAdmin/product/variant";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { loadDiscountOptions, loadPaymentOptions, setRegisterSheet } from "redux/actions/facility";
import { loadCart, postCart, postCartVariants } from "redux/actions/cart";
import CartMenuNew from "./CartMenuNew";
import ProductsNew from "./ProductsNew";
import { useRegisterContext } from "./RegisterContext";
import Spin from "components/spin/spin";
import Callout from "components/callout/Callout";
import { GetFolder, IFolder } from "api/rpc/2024-04/facilityAdmin/product/folder";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetCart } from "api/rpc/2024-04/facilityAdmin/order/cart/cart";
import { GetOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import { TFunction } from "i18next";
import Sheet from "components/sheet/Sheet";
import { ICustomer } from "redux/reducers/models/customer";
import { GetCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import SelectCustomer from "elements/register/SelectCustomer";
import { ICart } from "redux/reducers/models/cart";
import { SaveCartAsTab } from "api/rpc/2024-04/facilityAdmin/cart/cart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { displayCurrency } from "helpers/Helpers";
import MessageDisplay from "components/messageDisplay/MessageDisplay";

export interface IMainState {
  products: IProduct[];
  productsLoadingMessage: string;
  folders: IFolder[];
  selectedFolder: IFolder;
  unPaidOrder: IOrder;
  order: IOrder;
}

export interface IFilterState {
  search: string;
  barcodeSearch: boolean;
  sort: string;
  openNewTab: boolean;
  openViewTabs: boolean;
  newTabDescription: string;
  newTabCustomer: ICustomer;
  customerSearchQuery: string;
  customerSearchResults: (Partial<ICustomer> | ICustomer)[];
  currentCart: ICart;
  registerTabs: ICart[];
  showRegisterTabs: boolean;
  reloadRegisterTabs: boolean;
  tabSearch: string;
  updatedTab: ICart;
}

export interface ITerminalState {
  online: boolean;
  connecting: boolean;
  reader: any;
}

interface ILocationState {
  params: { cartId: string; orderId: number };
}

export default function RegisterNew() {
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { facilityStore, cartStore } = useAppSelector(store => store);
  const registerGroup = useLocalStorage<IRegisterGroup>("register_group");
  const defaultFolderId = useLocalStorage<number>("default_folder_id");
  const { addLoadingVariant, removeLoadingVariant } = useRegisterContext();
  const history = useHistory();

  const [state, setState] = useState<IMainState>({
    products: null,
    productsLoadingMessage: null,
    folders: null,
    selectedFolder: null,
    unPaidOrder: null,
    order: null,
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    search: "",
    barcodeSearch: false,
    sort: "alpha",
    openNewTab: false,
    openViewTabs: false,
    newTabDescription: "",
    newTabCustomer: null,
    customerSearchQuery: "",
    customerSearchResults: [null],
    currentCart: null,
    registerTabs: [],
    showRegisterTabs: false,
    reloadRegisterTabs: false,
    tabSearch: "",
    updatedTab: null,
  });

  useEffect(() => {
    //Show warning dialog box when browser is refreshed
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      event.returnValue = "";
      return "";
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    void loadRegisterCart();
    void checkSelectedRegister();
    void loadRegisterTabs();
    if (facilityStore?.paymentOptions?.length === 0) {
      void dispatch(loadPaymentOptions());
    }
    if (facilityStore?.discountOptions?.length === 0) {
      void dispatch(loadDiscountOptions());
    }
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (registerGroup) {
      void loadFolders(registerGroup.id, source.token);
    }
    return () => source.cancel();
  }, [registerGroup]);

  useEffect(() => {
    if (filterState.reloadRegisterTabs) {
      void loadRegisterTabs();
      setFilterState(prevState => ({ ...prevState, reloadRegisterTabs: false }));
    }
  }, [filterState?.reloadRegisterTabs]);

  // Used for searching all products
  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    const search = () => {
      timeoutId = global.setTimeout(() => {
        void (() => {
          try {
            if (filterState.search === "") {
              if (mounted) {
                updateMainState({ products: undefined });
              }
              return;
            } else {
              if (mounted) {
                void loadProducts(source.token);
              }
            }
          } catch (error) {
            console.log("err", error);
          }
          return;
        })();
      }, 500);
    };
    search();
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      source.cancel();
    };
  }, [filterState.search]);

  //Used for searching all customers to add to a tab
  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    if (mounted === true) {
      timeoutId = global.setTimeout(() => {
        void searchForCustomers(mounted, filterState.customerSearchQuery, source.token);
      }, 300);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      setState(prevState => ({ ...prevState, customerSearchResults: [null] }));
      source.cancel("Cancelled");
    };
  }, [filterState.customerSearchQuery]);

  useEffect(() => {
    const tabs = [...filterState?.registerTabs];

    filterState?.registerTabs?.map((tab: ICart, index: number) => {
      if (filterState?.updatedTab?.id === tab.id) {
        tabs.splice(index, 1);
        tabs.push(filterState?.updatedTab);
      }
    });

    setFilterState(prevState => ({ ...prevState, registerTabs: tabs.sort((a, b) => (a.id < b.id ? -1 : 1)) }));
  }, [filterState?.updatedTab]);

  function checkSelectedRegister() {
    const register = JSON.parse(localStorage.getItem("register"));
    const registerId = register?.id ? register?.id : undefined;
    if (!registerId) {
      dispatch(setRegisterSheet(true));
    }
  }

  function updateMainState(newMainState: Partial<IMainState>) {
    setState((cur: IMainState) => {
      return { ...cur, ...newMainState };
    });
  }

  function updateFilterState(newFilterState: Partial<IFilterState>) {
    setFilterState(cur => {
      return { ...cur, ...newFilterState };
    });
  }

  async function loadFolders(registerGroupId: number, token?: CancelToken) {
    const folderRes = await GetFolder({ register_group_id: registerGroupId, extended: false }, false, token);

    if (token && token.reason) {
      return;
    }
    if (folderRes.status !== StatusCode.OK) {
      updateMainState({ folders: [] });
      dispatch(showError(t("secure.facility.register.register_new.register_new.001")));
      return;
    }

    const folders = folderRes.data;

    // Get default folder for the selected Register or the default set for the facility
    let selectedFolder: IFolder = undefined;

    if (defaultFolderId > 0) {
      selectedFolder = folders.find(folder => folder.id === defaultFolderId);
    } else {
      selectedFolder = folders.find(folder => folder.default === true);
    }

    if (!selectedFolder) {
      selectedFolder = folders?.[0];
    }

    updateMainState({ folders: folders });

    if (selectedFolder) {
      void handleChangeSelectedFolder(selectedFolder);
    }
  }

  async function loadProducts(cancelToken: CancelToken) {
    updateMainState({ productsLoadingMessage: t("secure.facility.register.register_new.register_new.002") });
    //Search products
    if (!filterState.barcodeSearch) {
      updateMainState({ products: undefined });
      const productRes = await GetProduct({ extended: true, search: filterState.search }, false, cancelToken);
      updateMainState({ productsLoadingMessage: null });
      if (productRes.status === StatusCode.OK) {
        updateMainState({ products: productRes.data });
      } else {
        updateMainState({ products: [] });
        if (cancelToken && cancelToken?.reason) {
          updateMainState({ productsLoadingMessage: null });
          return;
        }
        dispatch(showError(t("secure.facility.register.register_new.register_new.003")));
      }
    } else {
      //Cannot scan and add item to cart if an order is already in progress (partial payment made)
      if (state?.order) {
        dispatch(showError(t("secure.facility.register.register_new.register_new.004")));
        updateMainState({ productsLoadingMessage: null });
        return;
      }
      //Search variant by barcode and add to cart
      const res = await GetVariant({ extended: true, barcode: filterState.search }, false, cancelToken);
      updateMainState({ productsLoadingMessage: t("secure.facility.register.register_new.register_new.005") });
      if (res.status === StatusCode.OK) {
        const variants = res.data.filter(variant => variant.facility_access === true);

        if (variants.length == 1) {
          const variant = variants[0];
          void addLoadingVariant(variant, variant?.product);
          const updatedCart = await dispatch(
            postCartVariants(
              { cart_id: cartStore?.cart?.id, variants: [{ variant_id: variant?.id, quantity: 1, parent_id: null }] },
              false,
            ),
          );
          updateMainState({ productsLoadingMessage: null });
          void removeLoadingVariant(variant?.id);
          if (!updatedCart) {
            dispatch(showError(t("secure.facility.register.register_new.register_new.006")));
            return;
          }

          updateFilterState({ search: "" });
        } else {
          updateMainState({ productsLoadingMessage: null });
          dispatch(showError(t("secure.facility.register.register_new.register_new.007")));
        }
        updateMainState({ products: [] });
      } else {
        updateMainState({ productsLoadingMessage: null });
        if (cancelToken?.reason) {
          return;
        }
        dispatch(showError(t("secure.facility.register.register_new.register_new.008")));
      }
    }
  }

  async function loadRegisterCart() {
    const locationState = location.state as ILocationState;

    if (locationState?.params) {
      dispatch(enqueue());
      //Get cart if location state cart token exists
      const cartRes = await GetCart({ id: Number(locationState?.params?.cartId) }, false);
      if (cartRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.register.register_new.register_new.009")));
        dispatch(dequeue());
        return;
      }

      //Set the cart in redux without making another GET Cart call (tableCart used to avoid setting token in localStorage)
      void dispatch(loadCart({ tableCart: cartRes?.data[0] }, false));

      //Get the order so the user can continue to complete the order
      const orderRes = await GetOrder({ id: locationState?.params?.orderId, extended: true }, false);
      if (orderRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.register.register_new.register_new.010")));
        dispatch(dequeue());
        return;
      }
      dispatch(dequeue());
      setState(prevState => ({ ...prevState, unPaidOrder: orderRes?.data[0] }));
    } else {
      void dispatch(loadCart({ tableCart: null }, false));
      if (localStorage.getItem("register_cart_id")) {
        await dispatch(loadCart({ id: Number(localStorage.getItem("register_cart_id")) }, false));
      } else if (localStorage.getItem("register_cart_token")) {
        await dispatch(loadCart({ token: String(localStorage.getItem("register_cart_token")) }, false));
      } else {
        await dispatch(postCart(null, false));
      }
    }
  }

  async function handleChangeSelectedFolder(selectedFolder: IFolder) {
    if (selectedFolder?.id === undefined) {
      return;
    }

    const storedProductFolders: IFolder[] = JSON.parse(localStorage.getItem(PRODUCT_FOLDERS_KEY)) ?? [];
    const storedProductFolder = storedProductFolders.find(productFolder => productFolder?.id === selectedFolder.id);

    const selectedFolderDate = new Date(selectedFolder.updated_at);
    const storedProductFolderDate = new Date(storedProductFolder?.updated_at);

    const storedProductFolderIsValid =
      !isNaN(selectedFolderDate?.getTime()) &&
      !isNaN(storedProductFolderDate?.getTime()) &&
      storedProductFolderDate.getTime() >= selectedFolderDate.getTime();

    if (storedProductFolderIsValid) {
      updateMainState({ selectedFolder: storedProductFolder });
    } else {
      const folderRes = await GetFolder({ id: selectedFolder.id, extended: true }, true);

      if (folderRes.status !== StatusCode.OK || folderRes.data?.length === undefined || folderRes.data.length === 0) {
        dispatch(showError(t("secure.facility.register.register_new.register_new.011")));
        return;
      }

      const newSelectedFolder = folderRes.data[0];

      if (!storedProductFolder) {
        const updatedStoredProductFolders = [...storedProductFolders, newSelectedFolder];
        localStorage.setItem(PRODUCT_FOLDERS_KEY, JSON.stringify(updatedStoredProductFolders));
      } else {
        const updatedStoredProductFolders = storedProductFolders.map(productFolder => {
          if (productFolder.id === newSelectedFolder.id) {
            return newSelectedFolder;
          } else {
            return productFolder;
          }
        });

        localStorage.setItem(PRODUCT_FOLDERS_KEY, JSON.stringify(updatedStoredProductFolders));
      }

      updateMainState({ selectedFolder: newSelectedFolder });
    }
  }

  function handleBarcodeSearchClick() {
    const productSearchId = document.getElementById("productSearchId");
    productSearchId.focus();
    updateFilterState({ barcodeSearch: !filterState.barcodeSearch });
  }

  function refreshFolderStorage() {
    localStorage.removeItem(PRODUCT_FOLDERS_KEY);

    if (state.selectedFolder) {
      void handleChangeSelectedFolder(state.selectedFolder);
    }

    dispatch(showSuccess(t("secure.facility.register.register_new.register_new.012")));
  }

  function renderProducts() {
    if (filterState.search && state.products) {
      const products = state.products?.sort((firstProd: IProduct, secondProd: IProduct) => {
        if (filterState.sort === "alpha") {
          if (firstProd.title < secondProd.title) {
            return -1;
          } else if (firstProd.title > secondProd.title) {
            return 1;
          } else {
            return 0;
          }
        }
      }) as [];
      return { useGridPositions: false, products };
    } else {
      const products = state.selectedFolder?.products ?? ([] as []);
      return { useGridPositions: true, products };
    }
  }

  async function loadRegisterTabs() {
    const res = await GetCart({ is_tab: true }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError("Error loading register tabs"));
      return;
    }

    setFilterState(prevState => ({ ...prevState, registerTabs: res.data }));
  }

  function setActiveRegisterTab(cart: ICart) {
    dispatch({
      type: "cart.update",
      payload: {
        isLoaded: true,
        cart: cart,
      },
    });

    localStorage.setItem("register_cart_id", String(cart.id));
    setFilterState(prevState => ({ ...prevState, showRegisterTabs: false }));
  }

  async function searchForCustomers(mounted: boolean, customerSearchQuery: string, cancelToken: CancelToken) {
    try {
      if (customerSearchQuery === "") {
        if (mounted) {
          setState(prevState => ({ ...prevState, customerSearchResults: [null], searchingCustomer: false }));
        }
        return;
      } else {
        setState(prevState => ({ ...prevState, searchingCustomer: true }));
        const res = await loadCustomers(customerSearchQuery, cancelToken);
        if (mounted) {
          setState(prevState => ({ ...prevState, customerSearchResults: res, searchingCustomer: false }));
        }
      }
    } catch (error) {
      setState(prevState => ({ ...prevState }));
    }
    return;
  }

  async function loadCustomers(customerSearchQuery: string, cancelToken: CancelToken) {
    const customerRes = await GetCustomer({ search: customerSearchQuery }, false, cancelToken);

    if (customerRes.status !== StatusCode.OK) {
      return [];
    }

    return customerRes.data as Array<Record<string, any>>;
  }

  const handleCustomerSelection = (customer: ICustomer) => {
    setFilterState(prevState => ({ ...prevState, newTabCustomer: customer }));
  };

  async function handleCreateRegisterTab() {
    const params = {
      cart_id: filterState?.currentCart?.id,
      customer_id: filterState?.newTabCustomer ? filterState?.newTabCustomer?.id : null,
      description: filterState?.newTabDescription,
    };

    const tabRes = await SaveCartAsTab(params, true);

    if (tabRes.status !== StatusCode.OK) {
      dispatch(showError("Error saving cart as tab"));
      return;
    }

    setFilterState(prevState => ({
      ...prevState,
      openNewTab: false,
      currentCart: null,
      newTabCustomer: null,
      newTabDescription: "",
    }));

    setActiveRegisterTab(tabRes.data);
    void loadRegisterTabs();
    dispatch(showSuccess("Successfully saved tab"));
  }

  function handleTabUpdate(cart: ICart) {
    setFilterState(prevState => ({ ...prevState, updatedTab: cart }));
  }

  return (
    <>
      <div className="ui-register">
        {!registerGroup ? (
          <RegisterNotFound t={t} />
        ) : !state?.folders ? (
          <div style={{ display: "flex", height: "100%", margin: "auto" }}>
            <div className="text-center m-auto">
              <div style={{ height: "32px" }}>
                <Spin />
              </div>
              <div>{t("secure.facility.register.register_new.register_new.013")}</div>
            </div>
          </div>
        ) : (
          <>
            <div className="ui-register-folders_container">
              <FolderMenu
                options={state.folders}
                optionTitle="title"
                defaultSelected={state.folders.findIndex(folder => folder.id === state.selectedFolder?.id)}
                onClick={option => handleChangeSelectedFolder(option)}
              />
            </div>

            {filterState?.showRegisterTabs ? (
              <div className="ui-register-new-products-container">
                <div className="ui-register-tabs-overview-container">
                  <div className="ui-register-tabs-overview-container_breadcrumb_container">
                    <div
                      className="ui-register-tabs-overview-container_breadcrumb_button_container"
                      onClick={() => setFilterState(prevState => ({ ...prevState, showRegisterTabs: false }))}
                    >
                      <FontAwesomeIcon
                        icon={faArrowLeft}
                        className="ui-register-tabs-overview-container_breadcrumb_button_container_icon"
                      />{" "}
                      <p className="ui-register-tabs-overview-container_breadcrumb_button_container_text">
                        Back to register
                      </p>{" "}
                      {/* TODO: Translate */}
                    </div>

                    <div className="flex-grow">
                      <Input
                        id="tabSearchId"
                        value={filterState.tabSearch}
                        onChange={(value: any) => updateFilterState({ tabSearch: value.target.value })}
                        type="search"
                        placeholder={"Search saved tabs..."}
                        autoFocus
                        labelHidden
                      />
                    </div>
                  </div>

                  <div>
                    <p className="ui-register-tabs-overview-container_title">Saved tabs</p> {/* TODO: Translate */}
                    <p className="ui-register-tabs-overview-container_subtitle">
                      {filterState?.registerTabs?.length} {filterState?.registerTabs?.length === 1 ? "item" : "items"}
                    </p>
                  </div>

                  <div className="ui-register-tabs-overview-container_tabs_container">
                    {filterState?.registerTabs
                      ?.filter(
                        tab =>
                          tab?.description?.toLocaleLowerCase().includes(filterState?.tabSearch.toLocaleLowerCase()) ||
                          tab?.customer?.full_name
                            ?.toLocaleLowerCase()
                            .includes(filterState?.tabSearch.toLocaleLowerCase()),
                      )
                      ?.map((tab: ICart, index: number) => {
                        return (
                          <div
                            className="ui-register-tabs-overview-container_tab_container"
                            key={index}
                            onClick={() => setActiveRegisterTab(tab)}
                          >
                            <div className="flex justify-between items-center">
                              {tab.customer ? (
                                <div className="ui-register-cart-new-customer-title-container">
                                  <FontAwesomeIcon icon={faUser} className="ui-register-cart-new-customer-icon" />

                                  <p className="ui-register-cart-new-customer-title">{tab?.customer?.full_name}</p>
                                </div>
                              ) : (
                                <p className="ui-register-tabs-overview-container_tab_title">{tab?.description}</p>
                              )}

                              <div className="ui-register-tabs-overview-container_icon_container">
                                <FontAwesomeIcon icon={faArrowRight} className="cursor-pointer" />
                              </div>
                            </div>

                            <div className="flex justify-between items-center">
                              <div className="flex gap-2 items-center">
                                <FontAwesomeIcon
                                  icon={faShoppingCart}
                                  // onClick={}
                                  className="ui-register-tabs-overview-container_tab_items_icon"
                                />

                                <p className="ui-register-tabs-overview-container_tab_items_text">
                                  {tab?.line_items?.filter(lineItem => !lineItem.parent_id).length}{" "}
                                  {tab?.line_items?.length === 1 ? "item" : "items"}
                                </p>
                              </div>

                              <div className="ui-register-tabs-overview-container_tab_items_price">
                                {displayCurrency("cad", tab.total_price)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="ui-register-new-products-container">
                  <div className="ui-register-new-products-content">
                    <div className="ui-register-top-bar">
                      <div className="flex justify-between mb-4 gap-4">
                        <div className="flex-grow">
                          <Input
                            id="productSearchId"
                            value={filterState.search}
                            onChange={(value: any) => updateFilterState({ search: value.target.value })}
                            type="search"
                            placeholder={t("secure.facility.register.register_new.register_new.017")}
                            autoFocus
                            labelHidden
                            trailingButtons={[
                              <Button
                                key={1}
                                type="secondary"
                                active={filterState.barcodeSearch}
                                onClick={handleBarcodeSearchClick}
                              >
                                <Icon icon={faBarcodeRead} size="medium" />
                              </Button>,
                              <Button key={2} type="secondary" onClick={refreshFolderStorage}>
                                <Icon icon={faRefresh} size="medium" />
                              </Button>,
                            ]}
                          />
                        </div>
                        <div
                          className="ui-register-tabs-button"
                          onClick={() => setFilterState(prevState => ({ ...prevState, showRegisterTabs: true }))}
                        >
                          <p className="ui-register-tabs-button_quantity">{filterState?.registerTabs?.length}</p>
                          <p className="ui-register-tabs-button_text">Saved tabs</p> {/* TODO: Translate */}
                        </div>
                      </div>
                    </div>

                    <div className={"product-content"}>
                      {state.products?.length === 0 && filterState.search && state.productsLoadingMessage === null ? (
                        <MessageDisplay
                          title={t("secure.facility.register.register_new.register_new.019")}
                          icon={"magnifying-glass"}
                          content={t("secure.facility.register.register_new.register_new.020")}
                        />
                      ) : state?.selectedFolder?.products?.length === 0 && !filterState.search ? (
                        <MessageDisplay
                          title={t("secure.facility.register.register_new.register_new.021")}
                          icon={"circle-exclamation"}
                          content={t("secure.facility.register.register_new.register_new.022")}
                          actions={[
                            {
                              label: t("secure.facility.register.register_new.register_new.023"),
                              action: () => history.push(`/admin/settings/folders/${state?.selectedFolder?.id}`),
                            },
                          ]}
                        />
                      ) : (
                        <ProductsNew
                          {...renderProducts()}
                          disabled={
                            !cartStore?.cart ||
                            cartStore?.cart?.status === "complete" ||
                            state?.unPaidOrder ||
                            state.order
                              ? true
                              : false
                          }
                          cart={cartStore?.cart}
                          selectedFolder={state.selectedFolder}
                          productsLoadingMessage={state?.productsLoadingMessage}
                          updateTabCart={handleTabUpdate}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="ui-register-cart-menu ui-register-cart-menu-new ">
                  <CartMenuNew
                    dataOrigin={{
                      setRegisterState: updateMainState,
                      setFilterState: updateFilterState,
                      unPaidOrder: state.unPaidOrder,
                    }}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      <Prompt when={!!state?.order} message={() => `${t("secure.facility.register.register_new.register_new.014")}`} />

      <Sheet
        size={"small"}
        closable
        title={"Save as tab"} //TODO: Translate
        supportText="Name the tab or add a customer to save for later checkout" //TODO: Translate
        cancelText={"Cancel"} //TODO: Translate
        open={filterState.openNewTab}
        okText={filterState.currentCart?.is_tab ? "Save tab" : "Create tab"} //TODO: Translate
        okDisabled={filterState?.newTabDescription === "" && filterState?.newTabCustomer === null}
        backDropCancel={false}
        onOk={() => handleCreateRegisterTab()}
        onCancel={() =>
          setFilterState(prevState => ({
            ...prevState,
            openNewTab: false,
            currentCart: null,
            newTabCustomer: null,
            newTabDescription: "",
          }))
        }
        overflow
      >
        <div className="mt-3 mb-4">
          <Input
            id="newTabDescription"
            value={filterState?.newTabDescription}
            onChange={(value: any) =>
              setFilterState(prevState => ({ ...prevState, newTabDescription: value.target.value }))
            }
            placeholder={"Type in a name or brief description"} //TODO: Translate
            autoFocus
            label={"Description"} //TODO: Translate
          />
        </div>

        <div className="new-tab-separator">
          <p className="new-tab-separator_text"> or</p>
        </div>

        <div>
          {filterState?.newTabCustomer ? (
            <div>
              <p className="ui-register-new-tab-modal-customer-title">{"Customer Name"}</p>
              <div className="ui-register-new-tab-modal-customer-container">
                <div className="flex justify-center items-center">
                  <FontAwesomeIcon icon={faUser} className="ui-register-new-tab-modal-customer-icon" />
                </div>

                <div className="ui-register-new-tab-modal-customer-name-container">
                  <p className="ui-register-new-tab-modal-customer-name">{filterState?.newTabCustomer?.full_name}</p>
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="ui-register-new-tab-modal-customer-name-icon"
                    onClick={() => setFilterState(prevState => ({ ...prevState, newTabCustomer: null }))}
                  />
                </div>
              </div>
            </div> //TODO: Translate
          ) : (
            <SelectCustomer
              onCustomerChange={customer => handleCustomerSelection(customer)}
              permissionLevel={"facility"}
              newCustomer
              label="Customer name" //TODO: Translate
            />
          )}
          <p className="ui-register-new-tab-modal-customer-input-sub-text">Only one customer can be added per tab.</p>{" "}
          {/* TODO: Translate */}
        </div>
      </Sheet>
    </>
  );
}

export function RegisterNotFound(props: { t: TFunction }) {
  return (
    <div style={{ width: "100%" }}>
      <Callout
        title={props.t("secure.facility.register.register_new.register_new.015")}
        content={props.t("secure.facility.register.register_new.register_new.016")}
        type="error"
        actions={{
          primary: {
            label: props.t("secure.facility.register.register_new.register_new.018"),
            onClick: () =>
              window.open("https://support-cloud.teeon.com/hc/en-us/articles/20125325436429-Changing-a-Register"),
          },
        }}
      />
    </div>
  );
}
